import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Select from 'react-select'; // Language dropdown
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { ThemeContext } from '../context/ThemeContext';
import { ApiBaseUrlContext } from '../App';
import { ReactComponent as SidebarOpenIcon } from '../assets/svg/icon-sidebar-toggle.svg';
import { ReactComponent as SidebarCloseIcon } from '../assets/svg/icon-close.svg';
import { ReactComponent as ToggleThemeIcon } from '../assets/svg/icon-toggle-theme.svg';
import { ReactComponent as LogoutIcon } from '../assets/svg/icon-logout.svg';
import { ReactComponent as UserIcon } from '../assets/svg/icon-sidebar-user.svg';
import { ReactComponent as HelpIcon } from '../assets/svg/help-circle.svg';	
import { getHelpLink } from '../config/helpLinks';																			 
import './Sidebar.css';

const Sidebar = ({ isOpen, toggleSidebar, handleLogout, currentPath }) => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { t, i18n } = useTranslation();  
  const [allowedPages, setAllowedPages] = useState([]);  
  const [userRights, setUserRights] = useState('');     
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [language, setLanguage] = useState(localStorage.getItem('i18nextLng') || 'en');
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const location = useLocation();
  const [helpUrl, setHelpUrl] = useState(getHelpLink(currentPath));

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userid);
        setUserRights(decodedToken.user_rights);
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }

    
    const fetchUserData = async () => {
      try {
        const pagesResponse = await axios.get(
          `${apiBaseUrl}/mnt/api/roles/role-page-access/${userRights}`
        );
        setAllowedPages(pagesResponse.data);

        if (userId === 1) {
          setUserName('Admin');
        } else {
          const userResponse = await axios.get(`${apiBaseUrl}/mnt/api/technicians/${userId}`);
          const { userfirstname, userlastname } = userResponse.data;
          setUserName(`${userfirstname} ${userlastname}`);
        }
      } catch (error) {
        console.error('Error fetching user details or allowed pages:', error);
        setUserName(t('unknownUser'));
      }
    };

    if (userRights) {
      fetchUserData();
    }
  }, [apiBaseUrl, userRights, userId, t]);

  useEffect(() => {
    setHelpUrl(getHelpLink(currentPath)); // ✅ Update when path changes
  }, [currentPath]); // ✅ Dependency ensures update

  const handleLanguageChange = (selectedOption) => {
    setLanguage(selectedOption.value);
    i18n.changeLanguage(selectedOption.value);
    localStorage.setItem('language', selectedOption.value);
    localStorage.setItem('i18nextLng', selectedOption.value);
  };

  const languageOptions = [
    { value: 'en', label: <><img src="https://flagcdn.com/w40/gb.png" alt="EN" width="20" style={{ marginRight: 8 }} />English</> },
    { value: 'sv', label: <><img src="https://flagcdn.com/w40/se.png" alt="SV" width="20" style={{ marginRight: 8 }} />Swedish</> },
    { value: 'dk', label: <><img src="https://flagcdn.com/w40/dk.png" alt="DK" width="20" style={{ marginRight: 8 }} />Danish</> },
    { value: 'de', label: <><img src="https://flagcdn.com/w40/de.png" alt="DE" width="20" style={{ marginRight: 8 }} />German</> },
    { value: 'it', label: <><img src="https://flagcdn.com/w40/it.png" alt="IT" width="20" style={{ marginRight: 8 }} />Italian</> },
    { value: 'pt', label: <><img src="https://flagcdn.com/w40/pt.png" alt="PT" width="20" style={{ marginRight: 8 }} />Portuguese</> },
    { value: 'fr', label: <><img src="https://flagcdn.com/w40/fr.png" alt="FR" width="20" style={{ marginRight: 8 }} />French</> },
    { value: 'es', label: <><img src="https://flagcdn.com/w40/es.png" alt="ES" width="20" style={{ marginRight: 8 }} />Spanish</> },
    { value: 'cn', label: <><img src="https://flagcdn.com/w40/cn.png" alt="CN" width="20" style={{ marginRight: 8 }} />Chinese</> },
    { value: 'pl', label: <><img src="https://flagcdn.com/w40/pl.png" alt="CN" width="20" style={{ marginRight: 8 }} />Polish</> },
    { value: 'cz', label: <><img src="https://flagcdn.com/w40/cz.png" alt="CZ" width="20" style={{ marginRight: 8 }} />Czech</> },
    { value: 'hu', label: <><img src="https://flagcdn.com/w40/hu.png" alt="HU" width="20" style={{ marginRight: 8 }} />Hungarian</> },
    { value: 'ee', label: <><img src="https://flagcdn.com/w40/ee.png" alt="EE" width="20" style={{ marginRight: 8 }} />Estonian</> },
    { value: 'fi', label: <><img src="https://flagcdn.com/w40/fi.png" alt="FI" width="20" style={{ marginRight: 8 }} />Finnish</> },
  ];

  const isPageAllowed = (pageName) => {
    if (!Array.isArray(allowedPages)) {
      return false;
    }
    if (userId === 1) {
      return true;
    }
    return allowedPages.some((page) => page.page_name === pageName);
  };

  const sections = {
    [t('sidebar.errorReporting')]: [
      { pageName: 'Report Error', path: '/fault-error', label: t('reportError') },
      { pageName: 'Service Reports', path: '/service-reports', label: t('serviceReport') },
      { pageName: 'Time Reports', path: '/time-reports', label: t('sideTimeReports') },
    ],
    [t('sidebar.maintenance')]: [
      { pageName: 'Maintenance Schedule', path: '/maintenance-schedule', label: t('sideMaintenanceSchedule') },
    ],
    [t('sidebar.assets')]: [
      { pageName: 'Machines', path: '/machines', label: t('viewMachines') }, // ✅ Added Machines under "Assets"
      { pageName: 'Inventory', path: '/inventory', label: t('sideInventoryList') },
    ],
    [t('sidebar.setup')]: [
      { pageName: 'Personnel Management', path: '/personnel', label: t('sidePersonnelManagement') },
      { pageName: 'Add Machine', path: '/add-machine', label: t('manageMachines') },
      { pageName: 'Machine Priority', path: '/machine-priority', label: t('sideMachinePriority') },
      { pageName: 'Notification Input', path: '/notification-input', label: t('notification') },
      { pageName: 'Settings', path: '/settings', label: t('settings') },
    ],
  };

  const isSectionAllowed = (pages) =>
    pages.some((p) => isPageAllowed(p.pageName));

  return (
    <div>
      <div className="sidebar-container" onClick={toggleSidebar}>
        <div className="sidebar" onClick={(e) => e.stopPropagation()}>
          <div className="sidebar__inner">
            <div className="sidebar__top">
              <div className="sidebar__top-left">
                <div onClick={toggleSidebar} className="sidebar__close-button">
                  <SidebarCloseIcon />
                </div>
              </div>
              <div className="sidebar__top-right">
                <div onClick={toggleTheme} className="sidebar__toggle-theme-button">
                  <ToggleThemeIcon />
                </div>
                <div onClick={handleLogout} className="sidebar__logout-button">
                  <LogoutIcon />
                </div>
                <div className="sidebar__logout-button">
                  <a href={helpUrl} target="_blank" rel="noopener noreferrer">
                    <HelpIcon />
                  </a>
                </div>
              </div>
            </div>

            {isPageAllowed('Dashboard') && (
              <Link to="/" className="sidebar__link" onClick={toggleSidebar}>
                {t('dashboard')}
              </Link>
            )}

            {Object.entries(sections).map(([sectionTitle, pages]) => {
              if (!isSectionAllowed(pages)) {
                return null;
              }

              return (
                <React.Fragment key={sectionTitle}>
                  <div className="sidebar__link-section">
                    {sectionTitle}
                  </div>

                  {pages.map((page) =>
                    isPageAllowed(page.pageName) ? (
                      <Link
                        key={page.pageName}
                        to={page.path}
                        className="sidebar__link"
                        onClick={toggleSidebar}
                      >
                        {page.label}
                      </Link>
                    ) : null
                  )}
                </React.Fragment>
              );
            })}

            {/* 🌐 Language Selector */}
            <div className="sidebar__language-selector">
              <label htmlFor="language">{t('settingsPage.language')}</label>
              <Select
                id="language"
                value={languageOptions.find((opt) => opt.value === language)}
                onChange={handleLanguageChange}
                options={languageOptions}
                className="entry-select"
                classNamePrefix="entry-select"
                menuPortalTarget={document.body}       /* ✅ Render at body level */
                menuPosition="fixed"                   /* ✅ Fixed positioning for mobile */
                menuShouldScrollIntoView={false}       /* ✅ Prevents auto-scroll that hides dropdown */
                isSearchable={false}                   /* 🛡️ Disable mobile keyboard trigger */
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({
                    ...base,
                    backgroundColor: theme === 'dark' ? '#4a4a4a' : '#fff',
                    borderColor: theme === 'dark' ? '#555' : '#ccc',
                    color: theme === 'dark' ? '#fff' : '#000',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: theme === 'dark' ? '#fff' : '#000',
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: theme === 'dark' ? '#3a3a3a' : '#fff',
                    color: theme === 'dark' ? '#fff' : '#000',
                    zIndex: 9999,
                    top: window.innerWidth < 480 ? '60px' : base.top, /* 🎯 Adjust for mobile top visibility */
                    position: window.innerWidth < 480 ? 'fixed' : 'absolute',
                    width: window.innerWidth < 480 ? '100%' : '100%',
                    left: 0,
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused
                      ? theme === 'dark'
                        ? '#555'
                        : '#f0f0f0'
                      : 'transparent',
                    color: theme === 'dark' ? '#fff' : '#000',
                  }),
                }}
              />
            </div>

          <div className="sidebar__bottom">
            <div className="sidebar__user-info">
              <div className='sidebar__user-info-icon'><UserIcon /></div>{t('loggedInAs')}: {userName}
            </div>
          </div>
          </div>
        </div>
      </div>

      <div
        onClick={toggleSidebar}
        className="top-menu__icon-button top-menu__sidebar-button"
      >
        <SidebarOpenIcon />
      </div>
    </div>
  );
};

export default Sidebar;
